/**
 * Product URLs. Every product answers on two paths (same page, the id is what counts):
 *   /מוצר/<id>-<hebrew-name>         the localized URL - canonical, used by the site's links and the sitemap
 *   /product/<id>-<transliterated>   an ASCII (Latin) URL for crawlers / places that mangle Hebrew
 * A wrong or missing name part (old /product/<id> links, a renamed product) is redirected to the right one.
 */

export type ProductUrlLang = 'he' | 'en';

export const PRODUCT_PATH_HE = 'מוצר';
export const PRODUCT_PATH_EN = 'product';

const MAX_SLUG = 80;

const tidy = (s: string) =>
  s
    .replace(/[^\p{L}\p{N}]+/gu, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, MAX_SLUG)
    .replace(/-+$/, '');

/** the product name as a Hebrew URL part: "פלאג אנאלי 2 כדורים" -> "פלאג-אנאלי-2-כדורים" */
export const hebrewSlug = (name: string) =>
  tidy(
    name
      .trim()
      .toLowerCase()
      .replace(/['"״׳`]/g, '')
  );

// consonants; vowel letters (א ה ו י ע) are handled in transliterate()
const HE_LATIN: Record<string, string> = {
  ב: 'b',
  ג: 'g',
  ד: 'd',
  ז: 'z',
  ח: 'ch',
  ט: 't',
  כ: 'k',
  ך: 'ch',
  ל: 'l',
  מ: 'm',
  ם: 'm',
  נ: 'n',
  ן: 'n',
  ס: 's',
  פ: 'p',
  ף: 'f',
  צ: 'tz',
  ץ: 'tz',
  ק: 'k',
  ר: 'r',
  ש: 'sh',
  ת: 't'
};
// ג' ז' צ' (with a geresh) = j, zh, ch
const GERESH: Record<string, string> = { ג: 'j', ז: 'zh', צ: 'ch', ץ: 'ch' };
const isHe = (c: string | undefined) => c !== undefined && c >= 'א' && c <= 'ת';

/** a readable Latin spelling of Hebrew (no vowel marks, so a heuristic): "גומיה לביצים" -> "gomia-lbitzim" */
export function transliterate(text: string): string {
  const s = text.toLowerCase().replace(/[״"]/g, '');
  let out = '';
  for (let i = 0; i < s.length; i++) {
    const c = s[i];
    const prev = s[i - 1];
    const next = s[i + 1];
    const first = !isHe(prev);
    const last = !isHe(next) && next !== "'" && next !== '׳';
    if (GERESH[c] && (next === "'" || next === '׳')) {
      out += GERESH[c];
      i++;
    } else if (HE_LATIN[c]) out += HE_LATIN[c];
    else if (c === 'א' || c === 'ע') out += 'a';
    else if (c === 'ה') out += first ? 'h' : last ? 'a' : 'h';
    else if (c === 'ו') {
      if (next === 'ו') i++;
      out += first || next === 'ו' ? 'v' : 'o';
    } else if (c === 'י') {
      if (next === 'י') {
        out += 'y';
        i++;
      } else out += first ? 'y' : 'i';
    } else if (c === "'" || c === '׳') continue;
    else out += c;
  }
  return out;
}

/** the product name as an ASCII URL part */
export const latinSlug = (name: string) =>
  tidy(
    transliterate(name)
      .normalize('NFKD')
      .replace(/[^\x20-\x7e]/g, ' ')
  );

/** /מוצר/12-<hebrew name> (default) or /product/12-<latin name>; the id alone when the name gives nothing */
export function productPath(p: { id: number; name: string }, lang: ProductUrlLang = 'he'): string {
  const slug = lang === 'he' ? hebrewSlug(p.name) : latinSlug(p.name);
  return `/${lang === 'he' ? PRODUCT_PATH_HE : PRODUCT_PATH_EN}/${p.id}${slug ? `-${slug}` : ''}`;
}

/** the same path percent-encoded, as it goes into <link>, sitemaps and Location headers */
export const productHref = (p: { id: number; name: string }, lang: ProductUrlLang = 'he') => encodeURI(productPath(p, lang));

/** "12-anything" -> 12 (the :id route param) */
export const productIdFromParam = (param: string | undefined) => {
  const m = /^(\d+)/.exec(param ?? '');
  return m ? Number(m[1]) : Number.NaN;
};

/** a (decoded) path -> which product URL it is, or undefined */
export function parseProductPath(path: string): { lang: ProductUrlLang; id: number; segment: string } | undefined {
  const m = new RegExp(`^/(${PRODUCT_PATH_HE}|${PRODUCT_PATH_EN})/((\\d+)[^/]*)/?$`).exec(path);
  return m ? { lang: m[1] === PRODUCT_PATH_HE ? 'he' : 'en', id: Number(m[3]), segment: m[2] } : undefined;
}
